// **Material UI
import { Box, Button, Checkbox, Tooltip, Typography } from '@mui/material'

// **React Hooks
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

// **API
import { generateShareableToken } from '../../../../axios/api/auth'

// **Components
import CustomDialog from '../../../../components/dialog'
import Icon from '../../../../components/icon'

// **Utilities
import { Controller, useForm } from 'react-hook-form'
import { TOOLTIPS } from '../../../../utils/tooltips'
import { copyToClipboard, showErrorToast } from '../../../../utils/utils'
import OverlayLoading from '../../../../components/overlay-loading'

const ControlCheckBox = memo(({ control, name, label, subLabel, icon }) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Box
          onClick={() => field?.onChange(!field.value)}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'left',
            gap: '5px',
            padding: '10px',
            border: `2px solid ${
              field?.value ? 'var(--primary-main)' : 'var(--grey-300)'
            }`,
            background: field?.value
              ? 'rgba(239, 246, 255, 1)'
              : 'var(--grey-50)',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          <Checkbox
            checked={field?.value}
            onChange={field?.onChange}
            onClick={(e) => e.stopPropagation()}
            size="small"
            sx={{ padding: '0px' }}
          />
          <Box
            sx={{
              display: 'flex',
              gap: '5px',
              flexDirection: 'column',
              marginTop: '4px'
            }}
          >
            <Box sx={{ display: 'flex', gap: '5px', alignItems: 'flex-end' }}>
              <Icon fontSize={'16px'} icon={icon} />
              <Typography
                sx={{ fontSize: '15px', fontWeight: '600', lineHeight: '1' }}
              >
                {label}
              </Typography>
            </Box>
            <Typography
              sx={{ fontSize: '13px', color: 'var(--secondary-main)' }}
            >
              {subLabel}
            </Typography>
          </Box>
        </Box>
      )}
    />
  )
})

const CandidatePublicLinkMetaDataModal = memo(
  ({
    open,
    title,
    handleClose,
    setIsGenerating,
    setIsExpired,
    setExpiryTime,
    handleCreateShareableLink,
    contestId,
    candidateId,
    setPublicLinkMetaDataState,
    isExpired,
    generatedUrl,
    publicLinkMetadataState,
    isGenerating
  }) => {
    const { control, handleSubmit, setValue, reset } = useForm({
      mode: 'onChange',
      defaultValues: {
        showDetails: false,
        showResume: false,
        showVideos: false
      }
    })

    const onSubmit = useCallback(
      async (data) => {
        try {
          const { showDetails, showResume, showVideos } = data
          setIsGenerating(true)
          const response = await generateShareableToken(
            contestId,
            candidateId,
            !showDetails,
            !showResume,
            !showVideos
          )
          setIsGenerating(false)

          if (response?.isError) {
            showErrorToast(response)
            return
          }
          handleCreateShareableLink(response.publicLinkToken)
          setExpiryTime(response.publicLinkExpiryTime)
          setPublicLinkMetaDataState({
            showDetails: !response?.publicLinkMetadata?.showDetails,
            showResume: !response?.publicLinkMetadata?.showResume,
            showVideos: !response?.publicLinkMetadata?.showVideos
          })
          setIsExpired(false)
          handleClose()
        } catch (error) {
          setIsGenerating(false)
          showErrorToast(error)
          handleClose()
        }
      },
      [
        contestId,
        candidateId,
        handleCreateShareableLink,
        setIsGenerating,
        setExpiryTime,
        setIsExpired,
        handleClose,
        setPublicLinkMetaDataState
      ]
    )

    useEffect(() => {
      if (open) {
        setValue('showDetails', publicLinkMetadataState?.showDetails)
        setValue('showResume', publicLinkMetadataState?.showResume)
        setValue('showVideos', publicLinkMetadataState?.showVideos)
      } else {
        reset()
      }
    }, [publicLinkMetadataState, setValue, open, reset])

    return (
      <CustomDialog
        open={open}
        title={title}
        handleClose={handleClose}
        outsideClikClose={false}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          {isGenerating && <OverlayLoading />}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Box sx={{ gap: '10px', display: 'flex', flexDirection: 'column' }}>
              <ControlCheckBox
                control={control}
                name="showDetails"
                label={'Hide Candidate Details'}
                subLabel={TOOLTIPS.showDetails}
                icon={'charm:person'}
              />
              <ControlCheckBox
                control={control}
                name="showResume"
                label={'Hide Candidate Resume'}
                subLabel={TOOLTIPS.showResume}
                icon={'mdi:paper-outline'}
              />
              <ControlCheckBox
                control={control}
                name="showVideos"
                label={'Hide Candidate Videos'}
                subLabel={TOOLTIPS.showVideo}
                icon={'gravity-ui:video'}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: '12px',
              marginTop: '12px'
            }}
          >
            <Button variant="outlined" type="submit">
              {generatedUrl && !isExpired ? 'Regenerate Link' : 'Generate Link'}
            </Button>
          </Box>
        </form>
      </CustomDialog>
    )
  }
)

const CreateShareableLink = ({
  loading = false,
  contestId,
  candidateId,
  existingUuid = null,
  publicLinkMetadata = {}
}) => {
  // States
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedUrl, setGeneratedUrl] = useState(null)
  const [expiryTime, setExpiryTime] = useState(null)
  const [isExpired, setIsExpired] = useState(false)
  const [daysLeft, setDaysLeft] = useState(null)
  const [openMetaDataModal, setOpenMetaDataModal] = useState(false)
  const [publicLinkMetaDataState, setPublicLinkMetaDataState] = useState({
    showDetails: false,
    showResume: false,
    showVideos: false
  })

  // Create and optionally copy link
  const handleCreateShareableLink = useCallback((uuid, copy = true) => {
    const url = `${window.location.origin}/share/${uuid}`
    setGeneratedUrl(url)
    if (copy) copyToClipboard(url)
  }, [])

  // Copy existing link
  const handleCopyLink = useCallback(() => {
    if (generatedUrl && !isExpired) {
      copyToClipboard(generatedUrl)
    }
  }, [generatedUrl, isExpired])

  const handleOpenMetaDataModal = useCallback(async () => {
    setOpenMetaDataModal(true)
  }, [])

  // Determine button text and icon
  const buttonValues = useMemo(() => {
    if (isGenerating || loading)
      return {
        btnText: 'Creating Link...',
        icon: 'line-md:loading-twotone-loop'
      }
    if (isExpired)
      return {
        btnText: 'Link Expired - Regenerate',
        icon: 'flowbite:refresh-outline'
      }
    if (generatedUrl && !isExpired)
      return { btnText: 'Copy & Share URL', icon: 'iconamoon:copy' }
    return { btnText: 'Create Shareable Link', icon: 'fluent:share-24-regular' }
  }, [isGenerating, loading, generatedUrl, isExpired])

  // Text for days left until expiry
  const getDaysLeftText = useMemo(() => {
    if (generatedUrl && !isExpired) {
      if (!daysLeft || daysLeft <= 0) return null
      if (daysLeft === 1) return '(expires in 1 day)'
      return `(expires in ${daysLeft} days)`
    }
    return null
  }, [daysLeft, generatedUrl, isExpired])

  const handleCloseMetaDataModal = useCallback(() => {
    setOpenMetaDataModal(false)
  }, [])

  // Reset state when candidate changes
  useEffect(() => {
    if (!candidateId || !contestId) return
    setGeneratedUrl(null)
    setExpiryTime(null)
    setIsExpired(false)
    setDaysLeft(null)
  }, [candidateId, contestId])

  // Load existing link if available
  useEffect(() => {
    if (existingUuid && existingUuid?.publicLinkToken) {
      handleCreateShareableLink(existingUuid?.publicLinkToken, false)
    }
    if (existingUuid?.publicLinkExpiryTime) {
      setExpiryTime(existingUuid?.publicLinkExpiryTime)
      setIsExpired(new Date(existingUuid?.publicLinkExpiryTime) <= new Date())
    }
  }, [existingUuid, handleCreateShareableLink])

  // Live expiry and days left calculation
  useEffect(() => {
    if (!expiryTime || !candidateId) return

    const updateDays = () => {
      const now = new Date()
      const expiry = new Date(expiryTime)
      const diffMs = expiry - now
      const expired = diffMs <= 0
      setIsExpired(expired)

      if (!expired) {
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
        setDaysLeft(diffDays)
      } else {
        setDaysLeft(0)
      }
    }

    updateDays()
    const interval = setInterval(updateDays, 1000)
    return () => clearInterval(interval)
  }, [expiryTime, candidateId])

  useEffect(() => {
    setPublicLinkMetaDataState({
      showDetails:
        publicLinkMetadata?.showDetails == null
          ? false
          : !publicLinkMetadata?.showDetails,
      showResume:
        publicLinkMetadata?.showResume == null
          ? false
          : !publicLinkMetadata?.showResume,
      showVideos:
        publicLinkMetadata?.showVideos == null
          ? false
          : !publicLinkMetadata?.showVideos
    })
  }, [publicLinkMetadata])

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Regenerate button */}
        {generatedUrl && !isExpired && (
          <Button
            startIcon={<Icon icon={'flowbite:refresh-outline'} fontSize={20} />}
            size="small"
            variant="outlined"
            onClick={handleOpenMetaDataModal}
            disabled={loading}
          >
            {'Regenerate Link'}
          </Button>
        )}

        {/* Main share button */}
        <Box
          sx={{
            display: 'flex',
            gap: '3px',
            alignItems: 'center',
            position: 'relative',
            mb: getDaysLeftText ? 2 : 0
          }}
        >
          <Tooltip
            title={
              <>
                <Box sx={{ padding: '5px 0px' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '5px',
                      alignItems: 'flex-start',
                      justifyContent: 'left'
                    }}
                  >
                    <Icon
                      icon={
                        publicLinkMetaDataState?.showDetails
                          ? 'mdi:checkbox-outline'
                          : 'raphael:checkbox'
                      }
                      size="small"
                    />
                    <Typography fontSize={'14px'} color={'white'}>
                      Hide Candidate Details
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: '5px' }}>
                    <Icon
                      icon={
                        publicLinkMetaDataState?.showResume
                          ? 'mdi:checkbox-outline'
                          : 'raphael:checkbox'
                      }
                      size="smalll"
                    />
                    <Typography fontSize={'14px'} color={'white'}>
                      Hide Candidate Resume
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: '5px' }}>
                    <Icon
                      icon={
                        publicLinkMetaDataState?.showVideos
                          ? 'mdi:checkbox-outline'
                          : 'raphael:checkbox'
                      }
                      size="smalll"
                    />
                    <Typography fontSize={'14px'} color={'white'}>
                      Hide Candidate videos
                    </Typography>
                  </Box>
                </Box>
              </>
            }
            {...((!generatedUrl || isExpired) && {
              disableHoverListener: true
            })}
            arrow
          >
            <Button
              startIcon={<Icon icon={buttonValues?.icon} fontSize={20} />}
              size="small"
              variant="outlined"
              onClick={
                generatedUrl && !isExpired
                  ? handleCopyLink
                  : handleOpenMetaDataModal
              }
              disabled={loading}
            >
              {buttonValues?.btnText}
            </Button>
            {/* Days left display */}
          </Tooltip>
          {getDaysLeftText && (
            <Typography
              component={'span'}
              variant="caption"
              color="textSecondary"
              sx={{ position: 'absolute', bottom: '-16px', right: 0 }}
            >
              {getDaysLeftText}
            </Typography>
          )}
        </Box>
      </Box>

      <CandidatePublicLinkMetaDataModal
        open={openMetaDataModal}
        title={'Privacy Controls for Shared Link'}
        handleClose={handleCloseMetaDataModal}
        setIsGenerating={setIsGenerating}
        setIsExpired={setIsExpired}
        setExpiryTime={setExpiryTime}
        handleCreateShareableLink={handleCreateShareableLink}
        contestId={contestId}
        candidateId={candidateId}
        setPublicLinkMetaDataState={setPublicLinkMetaDataState}
        generatedUrl={generatedUrl}
        isExpired={isExpired}
        publicLinkMetadataState={publicLinkMetaDataState}
        isGenerating={isGenerating}
      />
    </>
  )
}

export default memo(CreateShareableLink)
